export type PageSlot = number | 'ellipsis-start' | 'ellipsis-end';

/** Widest the page window gets: first page, last page and five slots around the current one. */
export const SLOT_MAX = 7;

/**
 * Below `lg` the control row is 328px wide at a 360px viewport, which fits five 32px slots between
 * the two arrows -- seven would overflow.
 */
export const SLOT_MAX_MOBILE = 5;

function clamp(value: number, min: number, max: number): number {
    return Math.min(Math.max(value, min), max);
}

function range(from: number, to: number): number[] {
    return Array.from({ length: to - from + 1 }, (_, index) => from + index);
}

function toPositiveInteger(value: number, fallback: number): number {
    return Number.isFinite(value) && value > 0 ? Math.floor(value) : fallback;
}

/**
 * The window's length is `min(pageCount, slotMax)` for every page of a given `pageCount`. The footer
 * renders each slot at a fixed size, so a constant length is what keeps the arrows from moving while
 * the operator clicks through pages.
 */
export function buildPageSlots(page: number, pageCount: number, slotMax: number = SLOT_MAX): PageSlot[] {
    const lastPage = Number.isFinite(pageCount) ? Math.floor(pageCount) : 0;
    if (lastPage <= 0) {
        return [];
    }
    if (lastPage <= slotMax) {
        return range(1, lastPage);
    }

    const current = clamp(toPositiveInteger(page, 1), 1, lastPage);
    const innerSize = slotMax - 2;
    const half = Math.floor(innerSize / 2);

    let start = current - half;
    let end = current + half;
    if (start < 2) {
        start = 2;
        end = start + innerSize - 1;
    }
    if (end > lastPage - 1) {
        end = lastPage - 1;
        start = end - innerSize + 1;
    }

    const slots: PageSlot[] = [1, ...range(start, end), lastPage];
    // An ellipsis takes a slot of its own, so it must stand for more than the single page it replaces.
    if (start > 2) {
        slots[1] = 'ellipsis-start';
    }
    if (end < lastPage - 1) {
        slots[slotMax - 2] = 'ellipsis-end';
    }
    return slots;
}

function normalizeDependency(value: unknown): unknown {
    if (value === undefined || value === null || value === '') {
        return null;
    }
    if (Array.isArray(value)) {
        const items = value.map(normalizeDependency).filter((item) => item !== null);
        return items.length > 0 ? items : null;
    }
    if (typeof value === 'object') {
        const entries = Object.entries(value)
            .map(([key, entry]) => [key, normalizeDependency(entry)] as const)
            .filter(([, entry]) => entry !== null)
            .sort(([a], [b]) => a.localeCompare(b));
        return entries.length > 0 ? Object.fromEntries(entries) : null;
    }
    return value;
}

/**
 * Comparable key for the footer's reset dependencies (filters, sorting, search).
 *
 * Plain `JSON.stringify` is too eager here: on mount the search bar and the filter header emit their
 * empty values (`undefined` becomes `''` or `{}`), which is not a change the user made and must not
 * send a deep-linked `?page=3` back to page 1. Empty-ish values therefore collapse to `null`, and
 * object keys are sorted so a reordered filter object does not read as a change either.
 */
export function buildDependencyKey(dependencies: unknown[]): string {
    return JSON.stringify(normalizeDependency(dependencies ?? []) ?? []);
}

/**
 * `null` means there is nothing to label. `page` is clamped against `pageCount` so a shrinking result
 * set cannot produce a start beyond the end.
 */
export function buildPageRange(
    page: number,
    perPage: number,
    totalItems: number,
    pageCount: number
): { start: number; end: number } | null {
    const total = Number.isFinite(totalItems) ? Math.floor(totalItems) : 0;
    const size = toPositiveInteger(perPage, 0);
    if (total <= 0 || size <= 0) {
        return null;
    }

    const lastPage = Math.max(1, toPositiveInteger(pageCount, 1));
    const current = clamp(toPositiveInteger(page, 1), 1, lastPage);
    const start = Math.min((current - 1) * size + 1, total);
    const end = Math.max(start, Math.min(total, current * size));
    return { start, end };
}
